import React from "react";
import { AbsoluteFill, interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { Backdrop, glow } from "./shared";

export const Scene3Answer: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  const fade = interpolate(frame, [durationInFrames - 20, durationInFrames], [1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

  const cardSp = spring({ frame, fps, config: SPRING.gentle });
  const cardOp = interpolate(cardSp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const cardScale = interpolate(cardSp, [0, 1], [0.92, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  // Type the answer progressively
  const answerFull = "Batch analysis is implemented as a worker-pool pattern in batch_runner.py (lines 42-118), coordinated through Redis streams. Each job is partitioned into shards of 50 items, processed in parallel by 8 workers. The design was finalized in the Jan-14 architecture thread after the initial approach failed at scale.";
  const chars = Math.floor(interpolate(frame, [30, 360], [0, answerFull.length], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  }));
  const shown = answerFull.slice(0, chars);

  const CITES = [
    { n: 1, src: "github:batch_runner.py:42-118", c: COLORS.neonPurple, delay: 380 },
    { n: 2, src: "github:redis_shards.py:210",     c: COLORS.neonPurple, delay: 405 },
    { n: 3, src: "slack:#architecture 2026-01-14", c: COLORS.neonPink,    delay: 430 },
    { n: 4, src: "confluence:/batch-design",       c: COLORS.neonBlue,    delay: 455 },
  ];

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fade, overflow: "hidden" }}>
      <Backdrop frame={frame} accent={COLORS.neonGreen} />

      {/* Label */}
      <div style={{
        position: "absolute", top: 80, left: 0, right: 0,
        textAlign: "center",
        opacity: cardOp,
      }}>
        <div style={{
          display: "inline-block",
          fontSize: 22,
          fontFamily: FONTS.mono,
          color: COLORS.neonGreen,
          letterSpacing: 4,
          textTransform: "uppercase",
          fontWeight: 800,
          padding: "10px 26px",
          borderRadius: 30,
          background: `${COLORS.neonGreen}15`,
          border: `1.5px solid ${COLORS.neonGreen}`,
          textShadow: glow(COLORS.neonGreen),
        }}>
          ✓ EAKC · Cited Answer
        </div>
      </div>

      {/* Big answer card */}
      <div style={{
        position: "absolute",
        top: 170,
        left: "50%",
        transform: `translateX(-50%) scale(${cardScale}) perspective(1600px) rotateX(3deg)`,
        width: 1600,
        opacity: cardOp,
      }}>
        <div style={{
          background: `linear-gradient(135deg, ${COLORS.neonGreen}12, ${COLORS.bgCard}ee)`,
          border: `2.5px solid ${COLORS.neonGreen}`,
          borderRadius: 26,
          padding: "48px 60px",
          boxShadow: `0 0 80px ${COLORS.neonGreen}55, 0 30px 60px rgba(0,0,0,0.6)`,
        }}>
          {/* Typed answer with inline citations rendered separately for color */}
          <div style={{
            fontSize: 34,
            fontFamily: FONTS.body,
            color: COLORS.textPrimary,
            lineHeight: 1.55,
            minHeight: 320,
          }}>
            {renderAnswerWithHighlights(shown)}
            {chars < answerFull.length && (
              <span style={{
                display: "inline-block",
                width: 3,
                height: 36,
                background: COLORS.neonGreen,
                marginLeft: 3,
                verticalAlign: "text-bottom",
                opacity: Math.sin(frame * 0.3) > 0 ? 1 : 0,
              }} />
            )}
          </div>

          {/* Citation chips */}
          <div style={{ display: "flex", gap: 14, marginTop: 36, flexWrap: "wrap" }}>
            {CITES.map((cite) => {
              const sp = spring({ frame: frame - cite.delay, fps, config: SPRING.bouncy });
              const op = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
              const sc = interpolate(sp, [0, 1], [0.5, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
              return (
                <div key={cite.n} style={{
                  opacity: op,
                  transform: `scale(${sc})`,
                  fontSize: 18,
                  fontFamily: FONTS.mono,
                  color: cite.c,
                  padding: "10px 20px",
                  borderRadius: 12,
                  background: `${cite.c}12`,
                  border: `1.5px solid ${cite.c}88`,
                  textShadow: glow(cite.c),
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}>
                  <span style={{ fontWeight: 900, opacity: 0.7 }}>[{cite.n}]</span>
                  {cite.src}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};

// Highlight specific phrases within the typed answer
function renderAnswerWithHighlights(text: string): React.ReactNode {
  const highlights: { match: string; color: string }[] = [
    { match: "batch_runner.py (lines 42-118)", color: COLORS.neonPurple },
    { match: "Redis streams",                    color: COLORS.neonCyan },
    { match: "8 workers",                         color: COLORS.neonYellow },
    { match: "Jan-14 architecture thread",       color: COLORS.neonPink },
  ];
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;
  while (remaining.length > 0) {
    let matched = false;
    for (const h of highlights) {
      const prefix = h.match.slice(0, Math.min(remaining.length, h.match.length));
      const idx = remaining.indexOf(prefix);
      if (idx !== -1 && prefix.length > 2) {
        // Full match or partial match at current position
        if (idx === 0) {
          const visibleChunk = remaining.slice(0, Math.min(h.match.length, remaining.length));
          if (h.match.startsWith(visibleChunk) || visibleChunk === h.match) {
            parts.push(
              <span key={key++} style={{
                color: h.color,
                fontWeight: 800,
                textShadow: `0 0 20px ${h.color}cc`,
              }}>
                {visibleChunk}
              </span>
            );
            remaining = remaining.slice(visibleChunk.length);
            matched = true;
            break;
          }
        }
      }
    }
    if (!matched) {
      // Find next highlight start
      let nextStart = remaining.length;
      for (const h of highlights) {
        const i = remaining.indexOf(h.match);
        if (i !== -1 && i < nextStart) nextStart = i;
      }
      parts.push(<span key={key++}>{remaining.slice(0, nextStart)}</span>);
      remaining = remaining.slice(nextStart);
    }
  }
  return <>{parts}</>;
}

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
